import React, { useState, useEffect } from 'react';
import '../styles/Header.css';
import useFooterBoundary from '../hooks/useFooterBoundary';
import AppSidebar from './AppSidebar';
import AccountMenu from './AccountMenu';
import LanguageToggle from './LanguageToggle';
import { FcGoogle } from 'react-icons/fc';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import { persistUserSession } from '../utils/authStorage';
import useLocale from '../hooks/useLocale';
import useUserPlan from '../hooks/useUserPlan';

export default function Header({ user, isMenuOpen = false, setIsMenuOpen, showMenu = true }) {
  const [internalMenuOpen, setInternalMenuOpen] = useState(false);
  const locale = useLocale();
  const footerInset = useFooterBoundary();
  const { isPro, isLoading: isLoadingPlan } = useUserPlan(user);
  const [isScrolled, setIsScrolled] = useState(false);

  const canToggleMenu = typeof setIsMenuOpen === 'function';
  const shouldShowMenu = showMenu && !!user;
  const menuOpen = shouldShowMenu ? (canToggleMenu ? isMenuOpen : internalMenuOpen) : false;
  const handleToggleMenu = () => {
    if (!shouldShowMenu) return;
    if (canToggleMenu) {
      setIsMenuOpen(!isMenuOpen);
    } else {
      setInternalMenuOpen((prev) => !prev);
    }
  };
  const headerShiftClass = '';

  useEffect(() => {
    if (!menuOpen) return;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        if (canToggleMenu) setIsMenuOpen(false);
        else setInternalMenuOpen(false);
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [menuOpen, canToggleMenu, setIsMenuOpen]);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleGoogleLogin = async () => {
    try {
      const credential = await signInWithPopup(auth, googleProvider);
      const normalizedUser = persistUserSession(credential.user);
      if (!normalizedUser) {
        throw new Error('Unable to resolve user profile after sign-in');
      }
      window.location.reload();
    } catch (error) {
      console.error('Header login failed:', error);
      window.alert('Login failed. Please try again.');
    }
  };

  return (
    <>
      <header className={`header${headerShiftClass}${isScrolled ? ' header--scrolled' : ''}`}>
        <div className="header-left">
          {shouldShowMenu && (
            <button
              type="button"
              className="app-menu-toggle"
              onClick={handleToggleMenu}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="app-navigation"
            >
              ☰
            </button>
          )}
          <div className={`title logo-title${headerShiftClass}`} onClick={() => window.location.href = '/'}>
            <img src="/brand-mark.svg" alt="Logo" className="logo-icon" />
            <span>Chord Analyzer</span>
          </div>
        </div>
        <div className="header-right">
          <LanguageToggle locale={locale} />
          {!user && (
            <button
              type="button"
              className="header-login-button"
              aria-label="Google login"
              onClick={handleGoogleLogin}
            >
              <FcGoogle className="header-login-google-icon" />
              <span>Login</span>
            </button>
          )}
          {user && <AccountMenu user={user} locale={locale} isPro={isPro} isLoadingPlan={isLoadingPlan} />}
        </div>
      </header>
      {shouldShowMenu && (
        <>
          <button type="button" className={`app-menu-backdrop${menuOpen ? " is-open" : ""}`} style={{ bottom: footerInset }} tabIndex={menuOpen ? 0 : -1} aria-hidden={!menuOpen} aria-label="Close menu" onClick={handleToggleMenu} />
          <AppSidebar user={user} overlay isOpen={menuOpen} bottomInset={footerInset} />
        </>
      )}
    </>
  );
}
